import React, { Fragment, useContext } from "react";
import isFunction from "lodash/isFunction";
import take from "lodash/take";
import styled from "styled-components";
import { Text } from "grommet";
import { MobileView } from "react-device-detect";
import BackLink from "../../components/Link/BackLink";
import PageBanner from "../../components/PageBanner/PageBanner";
import PageHeading from "../../components/PageHeading/PageHeading";
import ContentWithTags from "../../components/ContentWithTags/ContentWithTags";
import PreviewContext from "../../components/Layout/PreviewContext";
import SocialShare from "../../components/SocialShare/SocialShare";
import AdSenseAd from "../../components/AdNetworks/AdSenseAd";
import FeaturedContent from "../../components/FeaturedContent/FeaturedContent";
import { getHebrewDate } from "../../components/utils";
import withLayoutAndData from "./withLayoutAndData";
import { breakpoint, border, color } from "../../theme";
import { Content } from "./genericPage";
import { transformEdgesNodes, trackEvent } from "../../components/utils";
import { StyledFeaturedEvent } from "../../components/FeaturedEvents/FeaturedEvents";
import PageSection from "../../components/PageSection/PageSection";
import NewsletterForm from "../../components/NewsletterForm/NewsletterForm";

//TODO: comments from facebook?

const ContentContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;

	${breakpoint("tablet", true)`
		max-width: 78%;
	`}
`;

const PostMeta = styled.div`
	display: flex;
	font-weight: bold;
	margin-bottom: 30px;
	align-items: center;
`;

const MetaSeparator = styled.span`
	display: inline-block;
	height: 1.5rem;
	width: 1px;
	${border({ color: "brand-dark", width: "medium", size: "small" })`left`}
	margin: 0 8px;
`;

const PostId = styled.span`
	font-size: 8px;
	${color("light-6")}
`;

// type Props = {
// 	children?: Object[],
// 	page: Page,
// 	siteTitle?: string,
// 	noAds?: Boolean,
// 	similar?: Object[],
// 	options?: {
// 		showSimilar?: boolean,
// 		showInlineAd?: Boolean,
// 	},
// };

const SimilarContent = styled(FeaturedContent)`
	${border()`top`}
`;

const trackSimilarContentItemClick = (item) => {
	trackEvent("post events", "similiar content", `click on post: ${item.title}`, item.slug);
};

const trackSimilarProductClick = (item) => {
	trackEvent("post events", "Products", `click on product: ${item.title}`, item.slug);
};

export const GenericPostTemplate = (props) => {
	const {
		children,
		page,
		siteTitle,
		noAds = false,
		options = {},
		similar,
		similarProducts,
	} = props;

	const isPreview = useContext(PreviewContext);

	return <div>
		<PageBanner title={siteTitle}
			image={page.image}
			yPosition={page.imageYPosition}
			alt={page.imageAlt}
		/>

		<ContentWithTags
			tags={page.tags}
			sectionPath={page.sectionPath}
			tagContentType={page.templateKey}>

			<ContentContainer>
				<PageHeading>
					{page.title}
				</PageHeading>

				<PostMeta>
					{page.author &&
						<Fragment>
							<Text size="large" color="brand-dark">
								נכתב ע"י: {page.author}
							</Text>
							{page.rawDate && <Fragment>
								<MetaSeparator />
								<Text size="medium" color="brand-dark">
									{getHebrewDate(new Date(page.rawDate))}
								</Text>
							</Fragment>}
						</Fragment>}
					<Text size="large" color="brand-dark">
						{page.showDate && page.date && getHebrewDate(new Date(page.date))}
					</Text>
				</PostMeta>

				{!isPreview &&
					<SocialShare {...page} url={page.slug} />}

				{children ?
					(isFunction(children) ? children({ page }) : children) :
					<Content content={page.content} />}

				{!noAds && options.showInlineAd && !isPreview ? <MobileView>
					<AdSenseAd slot="6029934537" layout="in-article"
						style={{ textAlign: "center", width: "95%", }} />
				</MobileView> : null}

				{options.showSimilar && similarProducts && similarProducts.length ?
					<FeaturedContent
						title={options.similarProductsTitle || "מוצרים מומלצים"}
						items={similarProducts}
						containerJustify="center"
						ItemComponent={StyledFeaturedEvent}
						onItemClick={trackSimilarProductClick}
						hideItemDescription
					/> :
					null}

				{options.showSimilar && similar && similar.length ?
					<SimilarContent items={similar}
						onItemClick={trackSimilarContentItemClick}
						title="מאמרים נוספים" /> : null}

				{page.sectionPath &&
					<BackLink title={`בחזרה ל${page.pageType}`} path={page.sectionPath} />}

				<PostId>{page.postId}</PostId>
			</ContentContainer>
		</ContentWithTags>

		{!noAds && !isPreview ?
			<AdSenseAd slot="3582420545" format="auto" /> : null}

		<PageSection>
			<NewsletterForm />
		</PageSection>
	</div>;
};

// type GenericPostProps = {
// 	page: Object,
// 	similar?: ?Object[],
// 	data: Object,
// 	children?: Object[],
// 	options?: Object,
// };

const GenericPost = (props) => {
	const { data, children, ...rest } = props;

	return <GenericPostTemplate
		{...rest}
		siteTitle={data.siteTitle.frontmatter.title}>
		{children}
	</GenericPostTemplate>;
};

const filterOnSlug = (slug, posts) =>
	posts.filter((post) => post.slug !== slug);

const buildSimilarList = (data) => {
	let list;
	const slug = data.markdownRemark.fields.slug;

	if (slug && data.similar) {
		const similar = transformEdgesNodes(data.similar);
		//make sure current page item isnt part of the similar list
		list = take(filterOnSlug(slug, similar), 3);

		if (list.length < 3 && data.recent) {
			const recent = transformEdgesNodes(data.recent);
			list = list.concat(take(filterOnSlug(slug, recent), (3 - list.length)));
		}
	}

	return list;
};

const buildSimilarProductsList = (data) => {
	let list;
	const slug = data.markdownRemark.fields.slug;

	if (slug && data.similarProducts) {
		const products = transformEdgesNodes(data.similarProducts);
		//make sure current page item isnt part of the similar list
		list = take(filterOnSlug(slug, products), 3);
	}

	return list;
};

export default ({ type, sectionPath, children, ...options }) => {
	return withLayoutAndData(
		//transform data for the page
		({ data }) => ({
			page: {
				...data.markdownRemark.frontmatter,
				...data.markdownRemark.fields,
				id: data.markdownRemark.id,
				content: data.markdownRemark.html,
				pageType: type,
				sectionPath,
			},
			similar: buildSimilarList(data),
			similarProducts: buildSimilarProductsList(data),
		}),
		//provide additional open graph tags
		({ page }) => ({
			ogTags: {
				type: "article",
				"article:published_time": new Date(page.date).toISOString(),
				"article:tags": page.tags.join(","),
				description: page.descriptionRaw,
				"author": page.author || "מונטסורי בייבי"
			},
			//provide meta tags overrides
			titleTemplate: `%s | ${type}`,
			title: page.title,
			description: page.descriptionRaw,
		}), children, options)(GenericPost);
};
